/**
 * AdminDailyDigest — Super Admin control for the Daily Activity Digest.
 *
 * Matrix layout: one ROW per subject (activity). Each subject can have many
 * recipient users. Super Admin(s) are locked-on for every subject (checked,
 * non-removable). Delivered as an in-app notification at 09:00 IST Mon–Sat
 * (Monday combines Sat + Sun).
 */
import React, { useEffect, useMemo, useState } from 'react';
import AppShell from '@/components/AppShell';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Navigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  CalendarClock, Check, ChevronsUpDown, Lock, Save, Send, Eye, X, Users, ShieldCheck,
  FileText, Coins, Gem, RotateCcw, ShoppingCart, ArrowLeftRight, Loader2, PencilRuler, ClipboardList, Sparkles,
} from 'lucide-react';
import { DigestView } from '@/components/digest/DigestView';

const SUBJECT_ICON = {
  order_forms: FileText,
  cad_confirmed: PencilRuler,
  requirement_raised: ClipboardList,
  raw_material_issued: Coins,
  setting_done: Sparkles,
  qc_done: ShieldCheck,
  jewellery_received: Gem,
  goldsmith_returns: RotateCcw,
  purchases: ShoppingCart,
  transfers: ArrowLeftRight,
};
const SUBJECT_HINT = {
  order_forms: 'New order forms raised',
  cad_confirmed: 'Orders whose CAD design was confirmed',
  requirement_raised: 'Raw-material requirements raised on order forms',
  raw_material_issued: 'Gold, diamonds, colour stones & pearls issued to goldsmiths',
  setting_done: 'Orders whose Setting step was completed (visible to everyone)',
  qc_done: 'Orders whose Quality Check step was completed',
  jewellery_received: 'Finished jewellery received back, completing orders',
  goldsmith_returns: 'Raw material returned by goldsmiths',
  purchases: 'Stock purchased + purchase returns (vendor side — not shown to Sales)',
  transfers: 'Material moved between orders / to-from the unattributed pool',
};
const ROLE_LABEL = { super_admin: 'Super Admin', company_admin: 'Company Admin', sales_person: 'Sales Person', sales_manager: 'Sales Manager', user: 'User' };

export default function AdminDailyDigest() {
  const { isSuperAdmin } = useAuth();
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;
  return (
    <AppShell
      title="Daily Digest"
      subtitle="Choose who receives the previous day's activity summary. Automatic 09:00 IST delivery is currently turned off — use “Send now” to deliver manually."
    >
      <DigestSettings />
    </AppShell>
  );
}

function DigestSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [alwaysOn, setAlwaysOn] = useState([]);
  const [users, setUsers] = useState([]);
  const [schedule, setSchedule] = useState('');
  const [autoSend, setAutoSend] = useState(true);
  // selection: { [subjectKey]: Set(userId) } — non-locked users only.
  const [sel, setSel] = useState({});
  const [dirty, setDirty] = useState(false);

  // Preview dialog
  const [previewUser, setPreviewUser] = useState('self');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/system/digest-subscriptions');
      setSubjects(data.subjects || []);
      setAlwaysOn(data.always_on || []);
      setUsers(data.users || []);
      setSchedule(data.schedule || '');
      setAutoSend(data.auto_send_enabled !== false);
      const next = {};
      (data.subjects || []).forEach((s) => { next[s.key] = new Set(); });
      (data.users || []).forEach((u) => {
        if (u.locked) return; // SA enforced separately
        (u.subjects || []).forEach((k) => { if (next[k]) next[k].add(u.id); });
      });
      setSel(next);
      setDirty(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to load digest settings'));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const lockedUsers = useMemo(() => users.filter((u) => u.locked), [users]);
  const pickableUsers = useMemo(() => users.filter((u) => !u.locked), [users]);

  const toggle = (subjectKey, userId) => {
    setSel((prev) => {
      const nextSet = new Set(prev[subjectKey] || []);
      if (nextSet.has(userId)) nextSet.delete(userId); else nextSet.add(userId);
      return { ...prev, [subjectKey]: nextSet };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Invert the matrix → per-user subject list (non-locked users only).
      const perUser = new Map();
      pickableUsers.forEach((u) => perUser.set(u.id, []));
      Object.entries(sel).forEach(([key, set]) => {
        set.forEach((uid) => { if (perUser.has(uid)) perUser.get(uid).push(key); });
      });
      const subscriptions = Array.from(perUser.entries()).map(([user_id, subjects]) => ({ user_id, subjects }));
      const { data } = await api.put('/system/digest-subscriptions', { subscriptions });
      setUsers(data.users || []);
      setDirty(false);
      toast.success('Digest recipients saved');
    } catch (e) {
      toast.error(extractError(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/system/digest-subscriptions/run-now');
      const created = data?.created ?? 0;
      const recips = data?.recipients ?? 0;
      toast.success(
        `Digest sent for ${data?.period?.label || 'the period'} — ${created} new, ${recips} recipient${recips === 1 ? '' : 's'}.`,
        { description: created === 0 ? 'Everyone already has this period’s digest (no duplicates created).' : undefined },
      );
    } catch (e) {
      toast.error(extractError(e, 'Failed to send'));
    } finally {
      setSending(false);
    }
  };

  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewPayload(null);
    try {
      const params = previewUser && previewUser !== 'self' ? { user_id: previewUser } : {};
      const { data } = await api.get('/system/digest-preview', { params });
      setPreviewPayload(data);
    } catch (e) {
      toast.error(extractError(e, 'Failed to build preview'));
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) return <div className="text-slate-500 py-10" data-testid="digest-admin-loading">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="daily-digest-admin">
      {/* Info + actions bar */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-lg bg-[var(--diamond-bg)] text-[var(--diamond-icon)] flex items-center justify-center shrink-0">
              <CalendarClock size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--ink-navy)] flex items-center gap-2">
                Daily Activity Digest
                {autoSend
                  ? <Badge className="bg-emerald-50 text-emerald-700" data-testid="digest-autosend-badge-on">Auto ON</Badge>
                  : <Badge className="bg-amber-50 text-amber-700" data-testid="digest-autosend-badge-off">Auto OFF</Badge>}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{schedule}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 md:ml-auto">
            <div className="flex items-center gap-2">
              <Select value={previewUser} onValueChange={setPreviewUser}>
                <SelectTrigger className="h-9 w-[220px]" data-testid="digest-preview-user-select">
                  <SelectValue placeholder="Preview as…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="self">Preview as me (all companies)</SelectItem>
                  {pickableUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" className="h-9" onClick={openPreview} data-testid="digest-preview-button">
                <Eye size={14} className="mr-1.5" /> Preview
              </Button>
            </div>
            <Button variant="outline" className="h-9" onClick={sendNow} disabled={sending} data-testid="digest-send-now-button">
              {sending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} Send now
            </Button>
            <Button className="h-9 bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white" onClick={save} disabled={saving || !dirty} data-testid="digest-save-button">
              {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />} Save changes
            </Button>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500 bg-[var(--paper-warm)] border border-[var(--divider)] rounded-lg px-3 py-2">
          <ShieldCheck size={13} className="text-emerald-600 shrink-0" />
          Super Admin{lockedUsers.length > 1 ? 's' : ''} always receive every activity and cannot be removed. Sales roles only see their attributed customers’ activity.
        </div>
      </Card>

      {/* Subject rows */}
      <Card className="p-0 overflow-hidden">
        {subjects.map((s, idx) => {
          const Icon = SUBJECT_ICON[s.key] || FileText;
          const selectedIds = sel[s.key] || new Set();
          const selectedUsers = pickableUsers.filter((u) => selectedIds.has(u.id));
          const isAlwaysOn = alwaysOn.includes(s.key);
          return (
            <div
              key={s.key}
              className={`p-4 flex flex-col lg:flex-row lg:items-start gap-3 ${idx ? 'border-t border-[var(--divider)]' : ''}`}
              data-testid={`digest-subject-row-${s.key}`}
            >
              <div className="flex items-start gap-3 lg:w-[300px] shrink-0">
                <div className="h-9 w-9 rounded-lg bg-[var(--goldsmith-bg)] text-[var(--goldsmith-icon)] flex items-center justify-center shrink-0">
                  <Icon size={16} />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[var(--ink-navy)]">{s.label}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{SUBJECT_HINT[s.key]}</div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                {isAlwaysOn ? (
                  <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-orange-200 bg-orange-50/60 px-2 py-2 min-h-[44px]">
                    <Badge
                      variant="secondary"
                      className="gap-1 border border-orange-200 bg-orange-100 text-orange-700"
                      data-testid={`digest-alwayson-chip-${s.key}`}
                      title="Always on — this activity is visible to everyone in the Activity Digest and cannot be removed"
                    >
                      <Lock size={11} /> Everyone — always on
                    </Badge>
                    <span className="text-[11px] text-orange-700/80">Visible to all users; cannot be changed.</span>
                  </div>
                ) : (
                <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-[var(--divider)] bg-white px-2 py-2 min-h-[44px]">
                  {/* Locked Super Admin chips */}
                  {lockedUsers.map((u) => (
                    <Badge
                      key={u.id}
                      variant="secondary"
                      className="gap-1 border border-emerald-200 bg-emerald-50 text-emerald-700"
                      data-testid={`digest-locked-chip-${s.key}-${u.id}`}
                      title="Super Admin — always receives this"
                    >
                      <Lock size={11} /> {u.name}
                    </Badge>
                  ))}
                  {selectedUsers.map((u) => (
                    <Badge
                      key={u.id}
                      variant="secondary"
                      className="gap-1 border border-[var(--diamond-border)] bg-[var(--diamond-bg)] text-[var(--diamond-icon)]"
                      data-testid={`digest-recipient-chip-${s.key}-${u.id}`}
                    >
                      {u.name}
                      <button type="button" onClick={() => toggle(s.key, u.id)} className="ml-0.5 hover:text-rose-600" aria-label={`Remove ${u.name}`}>
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}

                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                        data-testid={`digest-add-recipients-${s.key}`}
                      >
                        <Users size={13} /> Add recipients <ChevronsUpDown size={12} />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[360px] p-0" align="end">
                      <Command>
                        <CommandInput placeholder="Search users…" data-testid={`digest-search-${s.key}`} />
                        <CommandList className="max-h-[320px]">
                          <CommandEmpty>No users found.</CommandEmpty>
                          {groupByCompany(pickableUsers).map(([company, list]) => (
                            <CommandGroup key={company} heading={company}>
                              {list.map((u) => (
                                <CommandItem
                                  key={u.id}
                                  value={`${u.name} ${u.email} ${company}`}
                                  onSelect={() => toggle(s.key, u.id)}
                                  data-testid={`digest-user-option-${s.key}-${u.id}`}
                                >
                                  <Check size={14} className={`mr-2 ${selectedIds.has(u.id) ? 'opacity-100 text-[var(--diamond-icon)]' : 'opacity-0'}`} />
                                  <div className="min-w-0">
                                    <div className="text-sm text-[var(--ink-navy)] truncate">{u.name}</div>
                                    <div className="text-[11px] text-slate-400 truncate">{u.email} · {ROLE_LABEL[u.role] || u.role}</div>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          ))}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                )}
                <div className="mt-1 text-[11px] text-slate-400" data-testid={`digest-recipient-count-${s.key}`}>
                  {isAlwaysOn
                    ? 'Everyone'
                    : `${lockedUsers.length + selectedUsers.length} recipient${(lockedUsers.length + selectedUsers.length) === 1 ? '' : 's'}`}
                </div>
              </div>
            </div>
          );
        })}
      </Card>

      {/* Preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>Digest preview</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="py-10 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={16} /> Building preview…</div>
          ) : (
            <DigestView payload={previewPayload} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function groupByCompany(users) {
  const map = new Map();
  users.forEach((u) => {
    const key = u.company_name || '—';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(u);
  });
  return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
}
